import React, { useState, useRef, useEffect, useCallback } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, ActivityIndicator, Platform } from 'react-native';
import { pdfViewerService } from '../../../../services/pdfViewerService';
import { FileText, Eye } from '../../../icons';

interface PDFRendererProps {
  fileUrl?: string;
  isLoading: boolean;
  hasError: boolean;
  fileName: string;
}

interface NativePdfProps {
  source: { uri: string };
  style?: object;
  onError?: () => void;
}

export const PDFRenderer: React.FC<PDFRendererProps> = ({
  fileUrl,
  isLoading,
  hasError,
  fileName,
}) => {
  const [previewRequested, setPreviewRequested] = useState(false);
  const [normalizedUrl, setNormalizedUrl] = useState<string | null>(null);
  const [normalizing, setNormalizing] = useState(false);
  const [normalizeError, setNormalizeError] = useState(false);
  const blobUrlRef = useRef<string | null>(null);
  const prevFileUrlRef = useRef<string | undefined>(fileUrl);

  // Revoke blob URL on unmount
  useEffect(() => {
    return () => {
      if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current);
    };
  }, []);

  const normalize = useCallback(
    async (url: string) => {
      setNormalizing(true);
      setNormalizeError(false);
      try {
        const sourceType = url.startsWith('data:') ? 'dataUrl' : 'base64';

        let normalized = await pdfViewerService.normalizePDFSource({
          type: sourceType,
          data: url,
          mimeType: 'application/pdf',
          fileName,
        });

        // Browsers block data: URLs in iframes — convert to a Blob URL instead.
        if (Platform.OS === 'web' && normalized.startsWith('data:')) {
          try {
            const commaIdx = normalized.indexOf(',');
            const base64 = normalized.slice(commaIdx + 1);
            const binary = atob(base64);
            const bytes = new Uint8Array(binary.length);
            for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
            const blob = new Blob([bytes], { type: 'application/pdf' });
            if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current);
            blobUrlRef.current = URL.createObjectURL(blob);
            normalized = blobUrlRef.current;
          } catch {
            setNormalizing(false);
            setNormalizeError(true);
            return;
          }
        }

        setNormalizedUrl(normalized);
        setNormalizing(false);
      } catch {
        setNormalizing(false);
        setNormalizeError(true);
      }
    },
    [fileName],
  );

  // Re-normalize if the file changes while preview is already open
  useEffect(() => {
    if (previewRequested && prevFileUrlRef.current !== fileUrl && fileUrl) {
      normalize(fileUrl);
    }
    prevFileUrlRef.current = fileUrl;
  }, [fileUrl, previewRequested, normalize]);

  const requestPreview = useCallback(() => {
    setPreviewRequested(true);
    setNormalizeError(false);
    if (fileUrl) normalize(fileUrl);
  }, [fileUrl, normalize]);

  if (hasError || normalizeError) {
    return (
      <View style={styles.center}>
        <Text style={styles.errorText}>Failed to load PDF</Text>
        <Text style={styles.errorSub}>{fileName}</Text>
        <TouchableOpacity style={styles.previewButton} onPress={requestPreview}>
          <Eye size={18} color="#fff" />
          <Text style={styles.previewButtonText}>Try again</Text>
        </TouchableOpacity>
      </View>
    );
  }

  // Not yet requested — show prompt card
  if (!previewRequested) {
    return (
      <View style={styles.center}>
        <FileText size={52} color="rgba(255,255,255,0.5)" />
        <Text style={styles.promptName} numberOfLines={2}>{fileName}</Text>
        <TouchableOpacity
          style={styles.previewButton}
          onPress={requestPreview}
          testID="pdf-preview-button"
          accessibilityRole="button"
          accessibilityLabel={`Preview ${fileName}`}
        >
          <Eye size={18} color="#fff" />
          <Text style={styles.previewButtonText}>Preview Document</Text>
        </TouchableOpacity>
      </View>
    );
  }

  if (isLoading || normalizing || !normalizedUrl) {
    return (
      <View style={styles.center}>
        <ActivityIndicator size="large" color="#fff" />
        <Text style={styles.loadingText}>Loading PDF…</Text>
      </View>
    );
  }

  // Web: render in an iframe using createElement to avoid TypeScript issues
  if (Platform.OS === 'web') {
    return (
      <View style={styles.container}>
        {React.createElement('iframe', {
          src: normalizedUrl,
          style: { flex: 1, width: '100%', height: '100%', border: 'none', background: '#1a1a1a' },
          title: fileName,
        })}
      </View>
    );
  }

  // Native: use react-native-pdf
  let Pdf: React.ComponentType<NativePdfProps> | null = null;
  try {
     
    Pdf = require('react-native-pdf').default;
  } catch { /* not available */ }

  if (!Pdf) {
    return (
      <View style={styles.center}>
        <Text style={styles.errorText}>PDF viewer not available</Text>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <Pdf
        source={{ uri: normalizedUrl }}
        style={styles.pdf}
        onError={() => setNormalizeError(true)}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#1a1a1a',
  },
  pdf: {
    flex: 1,
    width: '100%',
  },
  center: {
    flex: 1,
    backgroundColor: '#1a1a1a',
    alignItems: 'center',
    justifyContent: 'center',
  },
  errorText: {
    color: '#fff',
    fontSize: 16,
    marginBottom: 4,
  },
  errorSub: {
    color: 'rgba(255,255,255,0.6)',
    fontSize: 13,
  },
  promptName: {
    color: 'rgba(255,255,255,0.75)',
    fontSize: 14,
    marginTop: 16,
    marginBottom: 24,
    textAlign: 'center',
    paddingHorizontal: 32,
  },
  previewButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    backgroundColor: 'rgba(255,255,255,0.15)',
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.25)',
  },
  previewButtonText: {
    color: '#fff',
    fontSize: 15,
    fontWeight: '600',
  },
  loadingText: {
    color: 'rgba(255,255,255,0.7)',
    fontSize: 13,
    marginTop: 12,
  },
});
